import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Minus,
  Plus,
  FastForward,
  DollarSign,
  CheckCircle,
  ArrowRight,
  CreditCard,
  AlertTriangle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatScheduleDate } from '@/utils/formatScheduleDate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { nmiDeclineMessage } from '@/lib/nmiDeclineMessage';
import NmiCardOnFileDisplay from '@/components/defera-capital-nmi/NmiCardOnFileDisplay';
import BillingAddressFields, { EMPTY_BILLING_ADDRESS, validateBillingAddress } from '@/components/defera-capital-nmi/BillingAddressFields';
import { loadCollectJs } from '@/components/defera-capital-nmi/nmiCollectJsLoader';
import {
  NMI_FIELD_SHELL_CLASS,
  nmiCollectParentStyles,
  nmiCollectStyleConfig,
} from '@/components/defera-capital-nmi/nmiCollectFieldStyles';
import { extractNmiCardDisplayFromCollectJs, formatNmiCardBrandLabel, getNmiCardOnFile } from '@/utils/nmiCardDisplay';
import { cn } from '@/lib/utils';
import {
  MODAL_PRIMARY_BTN_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';
import { scheduleSummary } from '@/components/booking/scheduleSummary';

/**
 * Defera Capital — NMI Make a Payment form.
 *
 * Parallel of `DeferaZeroMakePaymentForm` (Stripe). Mounted by
 * `PaymentScheduleDisplay` when the booking is on the NMI processor track.
 *
 * Three modes (same vocabulary as the Stripe form so any downstream
 * analytics + the parent's `onSuccess({ current_schedule, proposed_schedule })`
 * payload shape stay processor-agnostic):
 *
 *   - `skip`   — pay N upcoming installments now. Stepper picks N. NMI
 *                charges N × per_payment against the vault.
 *   - `payoff` — pay off everything remaining. One charge, all open
 *                attempts marked superseded.
 *   - `custom` — pay any dollar amount; the next installment shrinks
 *                by the partial-credit remainder, earlier ones become
 *                "paid ahead" (status='superseded').
 *   - `past_due` — pay toward past due while the plan is paused. The
 *                only partial payment the server accepts in that state:
 *                the amount comes off the past-due rows first (a row it
 *                covers is settled, a row it only partly covers stays
 *                past due at the smaller amount), leftover flows into
 *                upcoming rows. The plan resumes on its own once nothing
 *                is past due — no Restart Plan needed. Default mode when
 *                the schedule reports open invoices (1OSUWO: $400 on a
 *                $689.81 delinquent installment).
 *
 * Card: NMI has one vault per customer, so there is no saved-card picker —
 * but any mode can be paid with a DIFFERENT card, charged once and never
 * stored (Collect.js token + billing address → `payment_token` on the
 * charge; the vault and the card on file are untouched). Swapping the card
 * that collects the plan is still the Update Card flow.
 */

const fmt = (cents) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ScheduleComparisonBlock({
  preview,
  strikethroughCount = 0,
  caption = 'Your schedule after this payment.',
  columnLabel = 'After',
  summarizeNonUniform = false,
}) {
  if (!preview?.current_schedule) return null;
  return (
    <div className="space-y-3">
      <p className="text-white/60 text-xs">{caption}</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
          <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current</p>
          <p className="text-white/50 text-xs font-normal mb-2">
            {scheduleSummary(preview.current_schedule, { summarizeNonUniform })}
          </p>
          <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
            {(preview.current_schedule.dates || []).map((d, i) => (
              <div
                key={`${d.date}-${i}`}
                className={cn(
                  'flex justify-between text-[11px]',
                  strikethroughCount > 0 && i < strikethroughCount && 'line-through opacity-50',
                )}
              >
                <span className={d.past_due ? 'text-rose-300/80' : 'text-white/25'}>
                  {formatScheduleDate(d.date, 'MMM d')}{d.past_due ? ' · past due' : ''}
                </span>
                <span className={d.past_due ? 'text-rose-200/80' : 'text-white/35'}>${fmt(d.amount_cents || 0)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
          <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">{columnLabel}</p>
          {preview.trip_fully_paid ? (
            <p className="text-brand-200/90 text-xs">Trip fully paid — no more payments due</p>
          ) : (
            <>
              <p className="text-brand-200/90 text-xs font-normal mb-2">
                {scheduleSummary(preview.proposed_schedule, { summarizeNonUniform })}
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {(preview.proposed_schedule?.dates || []).map((d, i) => (
                  <div key={`${d.date}-${i}`} className="flex justify-between text-[11px]">
                    <span className={d.past_due ? 'text-rose-300/80' : 'text-brand-300/50'}>
                      {formatScheduleDate(d.date, 'MMM d')}{d.past_due ? ' · past due' : ''}
                    </span>
                    <span className={d.past_due ? 'text-rose-200/80' : 'text-brand-200/70'}>${fmt(d.amount_cents || 0)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeferaCapitalNmiMakePaymentForm({
  tripId,
  flightData = null,
  onClose,
  onSuccess,
  openInvoices = [],
  openInvoicesTotalCents = 0,
  planRestartable = false,
  remainingCentsHint = 0,
  perPaymentCentsHint = 0,
  upcomingDates = [],
  onPaymentUncertain,
  /** Single attempt row the customer routed in via a rose "Pay now" click in
   *  `PaymentScheduleDisplay`. When set, the modal renders a focal "Past-due
   *  balance" banner at the top with a one-click Pay button that fires a
   *  `custom` charge for exactly this row's `amount_cents`. Matches the Stripe
   *  modal's banner-then-tabs pattern. Shape:
   *    `{ amount_cents, installment_number, attempted_at, gateway_response_text }`.
   *  Null on a plain "Make a Payment" open (tabs are the primary CTA in that case). */
  pendingSettlementRow = null,
}) {
  const hasPastDue = Array.isArray(openInvoices) && openInvoices.length > 0 && openInvoicesTotalCents > 0;
  /** Past-due disables Pay ahead / Principal only. Pay off stays ENABLED: the server's
   *  payoff mode settles every collectible row — including past-due and delinquent ones —
   *  in a single charge (see the PAYOFF EXEMPTION in defera-capital-nmi-charge-early-payment).
   *  Pre-fix, all three tabs were disabled here while the server also rejected payoff with
   *  `past_due_blocks_payment`, so a `payoff_required` trip (past-due, no pending rows) had
   *  literally no button that worked. */
  const allTabsDisabledByPastDue = hasPastDue;
  const totalUpcomingCount = Array.isArray(upcomingDates) ? upcomingDates.length : 0;
  const upcomingCap = Math.max(1, totalUpcomingCount);
  /** Cents for the focal "Pay $X" banner CTA when the customer arrived from a rose
   *  retry-pending row. Captured once at mount (not memoized off the prop) so a
   *  schedule refresh that lands mid-charge can't yank the amount out from under
   *  the in-flight request. */
  const pendingSettlementCents = Number(pendingSettlementRow?.amount_cents) || 0;
  const hasPendingSettlement = pendingSettlementCents > 0;
  const [mode, setMode] = useState(
    allTabsDisabledByPastDue
      ? 'past_due'
      : perPaymentCentsHint > 0 && totalUpcomingCount > 0 ? 'skip' : 'custom',
  );
  const [skipCount, setSkipCount] = useState(1);
  const [amount, setAmount] = useState('');
  /** Pay-ahead sub-modes (NMI parallel of the Stripe form). Both end up routing through the same
   *  NMI backend (`mode='custom'` is already greedy: covers full installments first, then shrinks
   *  the next still-open attempt by the leftover P). The "installments" sub-mode pre-fills the
   *  amount as N × per-payment; the "custom" sub-mode lets the customer type any dollar amount.
   *  See `DeferaZeroMakePaymentForm` for the matching state shape on the Stripe track. */
  const [payAheadSubMode, setPayAheadSubMode] = useState('installments');
  const [payAheadCustomAmount, setPayAheadCustomAmount] = useState('');
  /** Pay toward past due — starts at the full past-due balance (one tap = the
   *  same thing Restart Plan charges), editable down to $5. */
  const [pastDueAmount, setPastDueAmount] = useState(() => (hasPastDue ? (openInvoicesTotalCents / 100).toFixed(2) : ''));
  /** Different card (charged once, never stored). Collect.js hosted fields +
   *  billing address; the token rides on the charge body as `payment_token`. */
  const [useDifferentCard, setUseDifferentCard] = useState(false);
  const [tokenizationKey, setTokenizationKey] = useState(null);
  const [keyLoadError, setKeyLoadError] = useState(null);
  const [collectReady, setCollectReady] = useState(false);
  const [billing, setBilling] = useState({ ...EMPTY_BILLING_ADDRESS });
  const [billingErrors, setBillingErrors] = useState({});
  const onTokenRef = useRef(null);
  const cardOnFile = getNmiCardOnFile(flightData);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [charging, setCharging] = useState(false);
  const [error, setError] = useState(null);
  const payNonceRef = useRef(null);
  /** Monotonic preview sequence id. Each `fetchPreview` call captures the next value;
   *  responses whose id no longer matches `current` are dropped on arrival. Without
   *  this, a slow earlier preview could land AFTER a faster newer one and let the
   *  customer confirm a breakdown that doesn't match the typed amount. */
  const previewRequestIdRef = useRef(0);
  /** Closing the dialog makes every in-flight preview stale, so none lands on
   *  (or fetches for) a form that's gone. Same mechanism as a newer preview
   *  superseding an older one; see src/hooks/useIsMounted.js for why it matters. */
  useEffect(() => () => { previewRequestIdRef.current += 1; }, []);
  /** Cent amount associated with the current `preview` state. Used to disable Pay
   *  while the preview lags behind the typed amount (e.g. mid-debounce typing). */
  const [previewAmountCents, setPreviewAmountCents] = useState(null);

  /** Centralized preview reset. Bumps `previewRequestIdRef` so any in-flight fetch
   *  drops its response on arrival (handles the mid-tab-switch race where a stale
   *  Pay-ahead preview would otherwise repopulate the UI AFTER the user navigated
   *  to Principal). Always pair `setPreview(null)` with this when the reset is
   *  navigation-driven; the typing-debounce path in `fetchPreview` already bumps
   *  via `++previewRequestIdRef.current`. */
  const resetPreview = useCallback(() => {
    previewRequestIdRef.current += 1;
    setPreview(null);
    setPreviewAmountCents(null);
    setLoadingPreview(false);
  }, []);

  const customCents = useMemo(
    () => Math.max(0, Math.round(parseFloat(amount || '0') * 100) || 0),
    [amount],
  );
  const payAheadCustomCents = useMemo(
    () => Math.max(0, Math.round(parseFloat(payAheadCustomAmount || '0') * 100) || 0),
    [payAheadCustomAmount],
  );
  const pastDueCents = useMemo(
    () => Math.max(0, Math.round(parseFloat(pastDueAmount || '0') * 100) || 0),
    [pastDueAmount],
  );

  // Server-side preview (single source of math truth — mirrors the Stripe variant).
  const fetchPreview = useCallback(async (overrides = {}) => {
    if (!tripId) return;
    const effectiveMode = overrides.mode ?? mode;
    const effectiveSubMode = overrides.payAheadSubMode ?? payAheadSubMode;
    const body = { booking_id: tripId, mode: effectiveMode };
    /** Cent amount this preview is keyed on. Captured so we can stamp it alongside the
     *  resulting `preview` state and detect "preview lags typed amount" in the submit
     *  disable check below. */
    let amountForPreviewCents = 0;
    if ((effectiveMode === 'skip_installments' || effectiveMode === 'skip') && effectiveSubMode === 'custom') {
      /** Pay-ahead → Custom Amount on NMI: backend `mode='custom'` is already greedy (covers
       *  full installments first, then shrinks the next still-open attempt). We pass through
       *  the typed dollar amount; the response carries the new proposed schedule. */
      const cents = Math.max(1, Math.round((overrides.payAheadCustomAmount != null
        ? Math.round(parseFloat(overrides.payAheadCustomAmount) * 100)
        : payAheadCustomCents) || 0));
      if (cents <= 0) return;
      body.mode = 'custom';
      body.amount_cents = cents;
      amountForPreviewCents = cents;
    } else if (effectiveMode === 'skip_installments' || effectiveMode === 'skip') {
      body.mode = 'skip_installments';
      body.skip_count = Math.max(1, Math.floor(overrides.skipCount ?? skipCount));
    } else if (effectiveMode === 'custom') {
      const cents = Math.max(1, Math.round((overrides.amount ?? customCents) || 0));
      if (cents <= 0) return;
      body.amount_cents = cents;
      amountForPreviewCents = cents;
    } else if (effectiveMode === 'past_due') {
      const cents = Math.max(1, Math.round((overrides.pastDueCents ?? pastDueCents) || 0));
      if (cents <= 0) return;
      body.mode = 'past_due';
      body.amount_cents = cents;
      amountForPreviewCents = cents;
    }
    /** Capture this request's monotonic id BEFORE the network round-trip. If a newer
     *  fetchPreview starts before we get our response back, we'll drop ours on arrival. */
    const requestId = ++previewRequestIdRef.current;
    setLoadingPreview(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (requestId !== previewRequestIdRef.current) return;
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(deferaCapitalNmiUrl('preview-early-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (requestId !== previewRequestIdRef.current) {
        /** Stale: a newer fetchPreview started while we were waiting. Drop our response. */
        return;
      }
      if (!res.ok) {
        /** Structured "Pay off instead" recommendation from the server (custom amount
         *  exceeds remaining). Surface as preview state so the UI can render a Pay-off
         *  CTA. Matches the Stripe form. */
        if (data?.code === 'exceeds_remaining') {
          setPreview({ ...data, exceeds_remaining: true, mode: body.mode });
          setPreviewAmountCents(amountForPreviewCents);
          return;
        }
        throw new Error(data?.error || `Preview failed (${res.status})`);
      }
      setPreview(data);
      setPreviewAmountCents(amountForPreviewCents);
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) return;
      setError(err.message || 'Failed to load preview');
      setPreview(null);
      setPreviewAmountCents(null);
    } finally {
      if (requestId === previewRequestIdRef.current) setLoadingPreview(false);
    }
  }, [tripId, mode, payAheadSubMode, skipCount, customCents, payAheadCustomCents, pastDueCents]);

  /** Auto-preview Pay-off on tab entry. The preview's `payoff_amount_cents` is the
   *  authoritative number — it now includes past-due AND delinquent rows (which
   *  `remainingCentsHint` from the parent's schedule data may not, since the schedule's
   *  remaining is computed off the strict open set). Without this fetch, a past-due
   *  customer would see a payoff figure smaller than what the card actually gets hit for. */
  useEffect(() => {
    if (mode !== 'payoff' || !tripId) return undefined;
    fetchPreview({ mode: 'payoff' });
    return undefined;
  }, [mode, tripId, fetchPreview]);

  // Auto-preview Pay toward past due whenever the typed amount changes (debounced).
  useEffect(() => {
    if (mode !== 'past_due' || !tripId) return undefined;
    if (pastDueCents < 500) {
      resetPreview();
      setError(null);
      return undefined;
    }
    const t = setTimeout(() => { fetchPreview({ mode: 'past_due', pastDueCents }); }, 400);
    return () => clearTimeout(t);
  }, [mode, pastDueCents, tripId, fetchPreview, resetPreview]);

  /** Public Collect.js key for the different-card path (lazy — only fetched once
   *  the customer opts in). Same endpoint the checkout uses; no vault involved. */
  const ensureTokenizationKey = useCallback(async () => {
    if (tokenizationKey) return tokenizationKey;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Please sign in');
    const res = await fetch(deferaCapitalNmiUrl('create-tokenization-session'), {
      method: 'POST',
      headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
      body: JSON.stringify({ booking_id: tripId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.tokenization_key) throw new Error(data?.error || 'Could not start the card form');
    setTokenizationKey(data.tokenization_key);
    return data.tokenization_key;
  }, [tokenizationKey, tripId]);

  // Mount Collect.js hosted fields when the customer picks a different card.
  useEffect(() => {
    if (!useDifferentCard) {
      setCollectReady(false);
      return undefined;
    }
    let cancelled = false;
    setKeyLoadError(null);
    (async () => {
      try {
        const key = await ensureTokenizationKey();
        if (cancelled) return;
        const CollectJS = await loadCollectJs(key);
        if (cancelled) return;
        CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          tokenizationKey: key,
          fields: {
            ccnumber: { selector: '#dc-nmi-mp-ccnumber', title: 'Card Number', placeholder: 'Card number' },
            ccexp: { selector: '#dc-nmi-mp-ccexp', title: 'Expiration', placeholder: 'MM / YY' },
            cvv: { display: 'show', selector: '#dc-nmi-mp-cvv', title: 'CVV', placeholder: 'CVC' },
          },
          ...nmiCollectStyleConfig(),
          fieldsAvailableCallback: () => { if (!cancelled) setCollectReady(true); },
          callback: (resp) => { const cb = onTokenRef.current; if (cb) cb(resp); },
        });
      } catch (e) {
        if (!cancelled) setKeyLoadError(e.message || 'Could not load card form');
      }
    })();
    return () => { cancelled = true; };
  }, [useDifferentCard, ensureTokenizationKey]);

  /** Body fields for the chosen card. Card on file → nothing (the server charges
   *  the vault). Different card → validate the billing address, tokenize through
   *  Collect.js, and return `payment_token` + `billing_address` + `card_display`.
   *  Throws with a customer-readable message; callers surface it and stop. */
  const collectCardFields = async () => {
    if (!useDifferentCard) return {};
    const errs = validateBillingAddress(billing);
    setBillingErrors(errs);
    if (Object.keys(errs).length > 0) throw new Error('Add the billing address for the card you want to use.');
    if (!window.CollectJS || !collectReady) throw new Error('Card form not ready yet');
    const tokenResp = await new Promise((resolve, reject) => {
      onTokenRef.current = (resp) => {
        if (resp?.token) resolve(resp);
        else reject(new Error(resp?.error || 'We could not read that card. Check the number, expiration and CVV.'));
      };
      try { window.CollectJS.startPaymentRequest(); }
      catch (ex) { reject(ex); }
    });
    const cardDisplay = extractNmiCardDisplayFromCollectJs(tokenResp);
    return {
      payment_token: tokenResp.token,
      billing_address: {
        address1: billing.address1.trim(),
        ...(billing.address2?.trim() ? { address2: billing.address2.trim() } : {}),
        city: billing.city.trim(),
        state: billing.state,
        zip: billing.zip.trim(),
      },
      ...(cardDisplay ? { card_display: cardDisplay } : {}),
    };
  };

  // Auto-preview Pay-ahead → Installments on stepper change (debounced like the Stripe variant).
  useEffect(() => {
    if (mode !== 'skip' || payAheadSubMode !== 'installments' || !tripId) return undefined;
    const t = setTimeout(() => { fetchPreview({ mode: 'skip', payAheadSubMode: 'installments', skipCount }); }, 300);
    return () => clearTimeout(t);
  }, [mode, payAheadSubMode, skipCount, tripId, fetchPreview]);

  // Auto-preview Pay-ahead → Custom Amount whenever the typed amount changes (debounced).
  useEffect(() => {
    if (mode !== 'skip' || payAheadSubMode !== 'custom' || !tripId) return undefined;
    if (payAheadCustomCents <= 0) {
      resetPreview();
      return undefined;
    }
    /** Client-side $5 floor short-circuit. The server returns 400 below_minimum for
     *  cents < 500, and the previous code path surfaced that as a red error banner
     *  on every keystroke under $5. The inline amber "Minimum payment is $5.00"
     *  warning under the input is already shown; skipping the fetch here keeps the
     *  UI calm while the customer is still typing. */
    if (payAheadCustomCents < 500) {
      resetPreview();
      setError(null);
      return undefined;
    }
    const t = setTimeout(() => {
      fetchPreview({ mode: 'skip', payAheadSubMode: 'custom', payAheadCustomAmount });
    }, 500);
    return () => clearTimeout(t);
  }, [mode, payAheadSubMode, payAheadCustomCents, payAheadCustomAmount, tripId, fetchPreview, resetPreview]);

  // Single charge path branching on mode.
  const submitCharge = async () => {
    setError(null);
    let cents = 0;
    const body = { booking_id: tripId };
    if (mode === 'past_due') {
      if (pastDueCents < 500) {
        setError('Minimum payment is $5.00.');
        return;
      }
      body.mode = 'past_due';
      body.amount_cents = pastDueCents;
      cents = pastDueCents;
    } else if (mode === 'skip' && payAheadSubMode === 'custom') {
      /** NMI's `mode='custom'` backend is already greedy (covers full installments first, then
       *  shrinks the next still-open attempt's `amount_cents` by P). So this sub-tab simply
       *  routes the typed amount through the existing path.
       *
       *  $5 floor matches the server (`below_minimum` rejection) and the UI input warning.
       *  Block early so the customer doesn't see a confusing server toast for a click that
       *  the disabled button should already have blocked. */
      if (payAheadCustomCents < 500) {
        setError('Minimum payment is $5.00.');
        return;
      }
      body.mode = 'custom';
      body.amount_cents = payAheadCustomCents;
      cents = payAheadCustomCents;
    } else if (mode === 'skip') {
      body.mode = 'skip_installments';
      body.skip_count = Math.max(1, Math.floor(skipCount));
      cents = Math.max(100, skipCount * (perPaymentCentsHint || 0));
    } else if (mode === 'payoff') {
      body.mode = 'payoff';
      /** Prefer the preview's figure — it includes past-due/delinquent rows that the
       *  parent's remaining hint can miss. Client-side only; the server recomputes. */
      cents = Math.max(100, preview?.payoff_amount_cents || remainingCentsHint || 0);
    } else {
      body.mode = 'custom';
      body.amount_cents = customCents;
      cents = customCents;
    }
    if (cents <= 0) {
      setError('Enter an amount');
      return;
    }
    if (!payNonceRef.current) {
      payNonceRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    body.client_request_id = payNonceRef.current;

    setCharging(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      Object.assign(body, await collectCardFields());
      const res = await fetch(deferaCapitalNmiUrl('charge-early-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.code === 'would_exceed_remaining') {
          toast.error(data.error || 'Not enough installments left to skip that many.');
          return;
        }
        /** Past-due gate (server-enforced; the UI also disables tabs but this catches
         *  direct-API bypasses). Mirror copy with Stripe. */
        if (data?.code === 'past_due_blocks_payment') {
          toast.error(data.error || 'Pay your past-due balance first.');
          return;
        }
        if (data?.code === 'nothing_past_due') {
          toast.error(data.error || 'Nothing is past due on this trip anymore — use Pay ahead or Principal.');
          return;
        }
        if (data?.code === 'billing_address_required') {
          setError(data.error || 'Add the billing address for the card you want to use.');
          return;
        }
        /** Booking-level lock contention. Lease has a TTL so the contention clears
         *  on its own; nudge the customer to wait rather than throwing a generic
         *  "Payment failed". */
        if (data?.code === 'payment_in_progress') {
          toast.error(data.error || 'Another payment for this trip is already running. Please wait a moment and try again.');
          return;
        }
        if (res.status >= 500) {
          onPaymentUncertain?.();
        }
        // Translate NMI's raw vendor strings into customer-friendly toasts.
        // Keep the raw text in the rejected Error so the catch block can
        // still log + Sentry-report it as the dev-facing identifier.
        const friendly = nmiDeclineMessage(data, {
          defaultMessage: useDifferentCard
            ? "That card was declined — your card on file wasn't charged. Try another card, or use the card on file."
            : "We couldn't complete that payment. Please try again, or update your card.",
        });
        console.error('[defera-capital-nmi] make-payment error', {
          rawText: friendly.dev,
          error_type: data?.error_type,
          gateway_response: data?.gateway_response,
          gateway_response_code: data?.gateway_response_code,
        });
        const err = new Error(friendly.customer);
        err.nmiAction = friendly.action;
        throw err;
      }
      payNonceRef.current = null;
      let successMsg;
      if (data.trip_fully_paid) {
        successMsg = 'Trip fully paid!';
      } else if (mode === 'past_due') {
        const stillPastDue = Number(data.past_due_remaining_cents) || 0;
        successMsg = data.plan_resumed || stillPastDue <= 0
          ? `Past-due balance cleared — $${fmt(cents)} paid. Your plan resumes as scheduled.`
          : `$${fmt(cents)} applied to your past-due balance — $${fmt(stillPastDue)} still past due.`;
      } else if (mode === 'skip' && payAheadSubMode === 'custom') {
        /** Pay-ahead → Custom Amount: prefer the charge response's authoritative
         *  `skip_count` / `partial_credit_cents` (computed server-side from the actual
         *  attempts ledger). Fall back to the preview if those fields are missing
         *  (older deploys). Pre-fix this re-derived from `(perPaymentCentsHint × N)`
         *  which is wrong after a prior partial credit has left attempts at non-uniform
         *  amounts. */
        const skipCount = Number(data.skip_count) || Math.max(
          0,
          (preview?.current_schedule?.num_payments || 0) - (preview?.proposed_schedule?.num_payments || 0),
        );
        const partialCents = Number(data.partial_credit_cents)
          || Math.max(0, payAheadCustomCents - skipCount * (perPaymentCentsHint || 0));
        if (skipCount > 0 && partialCents > 0) {
          successMsg = skipCount === 1
            ? `Next installment paid early — $${fmt(partialCents)} credit applied to your next payment.`
            : `${skipCount} upcoming installments paid early — $${fmt(partialCents)} credit applied to your next payment.`;
        } else if (skipCount > 0) {
          successMsg = skipCount === 1 ? 'Next installment paid early.' : `${skipCount} upcoming installments paid early.`;
        } else if (partialCents > 0) {
          successMsg = `Credit of $${fmt(partialCents)} applied to your next payment.`;
        } else {
          successMsg = 'Payment successful. Schedule updated.';
        }
      } else if (mode === 'skip') {
        const sc = data.skip_count || skipCount;
        successMsg = sc === 1 ? 'Next installment paid early.' : `${sc} upcoming installments paid early.`;
      } else {
        successMsg = 'Payment successful. Schedule updated.';
      }
      toast.success(successMsg);
      onSuccess?.({
        current_schedule: preview?.current_schedule ?? null,
        proposed_schedule: preview?.proposed_schedule ?? null,
      });
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Payment failed';
      setError(msg);
      toast.error(msg, { description: err?.nmiAction || undefined });
      if (err?.name === 'TypeError' || /fetch|network|gateway/i.test(msg)) {
        onPaymentUncertain?.();
      }
    } finally {
      setCharging(false);
    }
  };

  /** Dedicated one-click handler for the focal "Past-due balance" banner CTA when the
   *  customer routed in from a rose retry-pending row in `PaymentScheduleDisplay`. We
   *  intentionally don't go through `submitCharge` (which derives the body from tab state)
   *  — this path is mode-state agnostic, sending `mode='custom'` + the exact row amount so
   *  the server greedy-applies it to the failing attempt. Equivalent role to Stripe's
   *  `handlePayPastDue` → `/invoices/{id}/pay`, except NMI doesn't have a single-invoice
   *  pay endpoint so we use `charge-early-payment` with `mode='custom'`. The greedy
   *  matcher will mark the earliest open attempt (= the retrying one the customer is
   *  trying to settle) as `superseded`, which is what we want. */
  const submitPendingSettlement = async () => {
    if (!hasPendingSettlement || charging || !tripId) return;
    setError(null);
    if (!payNonceRef.current) {
      payNonceRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    setCharging(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      const cardFields = await collectCardFields();
      const res = await fetch(deferaCapitalNmiUrl('charge-early-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({
          booking_id: tripId,
          mode: 'custom',
          amount_cents: pendingSettlementCents,
          client_request_id: payNonceRef.current,
          ...cardFields,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.code === 'past_due_blocks_payment') {
          /** Customer's retrying row tipped into terminal between the schedule fetch and the
           *  click — nudge them to Restart Plan so they don't keep retrying a blocked path. */
          toast.error(data.error || 'Pay your past-due balance via Restart Plan.');
          return;
        }
        if (data?.code === 'payment_in_progress') {
          toast.error(data.error || 'Another payment for this trip is already running. Please wait a moment and try again.');
          return;
        }
        if (res.status >= 500) onPaymentUncertain?.();
        const friendly = nmiDeclineMessage(data, {
          defaultMessage: useDifferentCard
            ? "That card was declined — your card on file wasn't charged. Try another card, or use the card on file."
            : "We couldn't complete that payment. Please try again, or update your card.",
        });
        console.error('[defera-capital-nmi] pending-settlement charge error', {
          rawText: friendly.dev,
          error_type: data?.error_type,
          gateway_response: data?.gateway_response,
          gateway_response_code: data?.gateway_response_code,
        });
        const err = new Error(friendly.customer);
        err.nmiAction = friendly.action;
        throw err;
      }
      payNonceRef.current = null;
      const installmentLabel = pendingSettlementRow?.installment_number
        ? `Installment ${pendingSettlementRow.installment_number}`
        : 'Past-due installment';
      toast.success(data.trip_fully_paid
        ? 'Trip fully paid!'
        : `${installmentLabel} paid — $${fmt(pendingSettlementCents)} charged.`);
      onSuccess?.({
        current_schedule: data?.current_schedule ?? null,
        proposed_schedule: data?.proposed_schedule ?? null,
      });
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Payment failed';
      setError(msg);
      toast.error(msg, { description: err?.nmiAction || undefined });
      if (err?.name === 'TypeError' || /fetch|network|gateway/i.test(msg)) {
        onPaymentUncertain?.();
      }
    } finally {
      setCharging(false);
    }
  };

  const skipChargeCents = mode === 'skip' && perPaymentCentsHint > 0
    ? Math.min(remainingCentsHint || perPaymentCentsHint * skipCount, skipCount * perPaymentCentsHint)
    : 0;
  const payoffCents = preview?.payoff_amount_cents ?? remainingCentsHint ?? 0;

  /** Switch to Pay-off when the chosen skip count exceeds what's remaining. Mirrors Stripe's
   *  "Pay off $X" CTA so the customer has a one-tap path to settle the trip. */
  const handlePayoffSwitch = () => {
    setMode('payoff');
    resetPreview();
    setError(null);
    setAmount('');
    setPayAheadCustomAmount('');
    payNonceRef.current = null;
  };

  /** Derive "Next charge moves" labels client-side from the preview's schedule dates. NMI's
   *  preview doesn't return explicit `current_next_charge_date` / `new_next_charge_date` (unlike
   *  Stripe Zero), so we read them off the first row of each schedule. */
  const nextChargeDates = (() => {
    if (mode !== 'skip' || !preview) return null;
    const curr = preview.current_schedule?.dates?.[0]?.date;
    const next = preview.proposed_schedule?.dates?.[0]?.date;
    if (!curr || !next) return null;
    try {
      return {
        from: format(parseISO(curr), 'MMM d, yyyy'),
        to: format(parseISO(next), 'MMM d, yyyy'),
        changed: curr !== next,
      };
    } catch {
      return null;
    }
  })();

  return (
    <div className="space-y-4">
      <style>{nmiCollectParentStyles('#dc-nmi-mp-ccnumber, #dc-nmi-mp-ccexp, #dc-nmi-mp-cvv')}</style>
      {/* Focal "Past-due balance" banner — matches the Stripe modal's rose CTA at the
          top of `DeferaZeroMakePaymentForm`. Two flavors of the same banner:
            - Terminal past-due (declined/error/delinquent in `openInvoices`): banner
              is read-only because NMI's `charge-early-payment` is server-side gated
              by `past_due_blocks_payment`; the customer has to clear past-due via the
              Restart Plan modal. We nudge them to close + restart.
            - Pending settlement (retrying row routed in from a rose "Pay now" click):
              banner shows a one-click "Pay $X" button that fires `submitPendingSettlement`
              directly — no Tab navigation, no Principal context. This is the Stripe-parity
              the customer expected when they clicked Pay now. */}
      {hasPendingSettlement && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-rose-300 text-xs font-normal uppercase tracking-wider">
                {pendingSettlementRow?.installment_number
                  ? `Past due — Installment ${pendingSettlementRow.installment_number}`
                  : 'Past-due balance'}
              </p>
              <p className="text-rose-100 text-base font-normal mt-1 tabular-nums">${fmt(pendingSettlementCents)}</p>
              <p className="text-rose-200/70 text-[11px] mt-1">
                {pendingSettlementRow?.gateway_response_text
                  ? `Last attempt: ${pendingSettlementRow.gateway_response_text}`
                  : "Your card was declined on the last attempt — pay now to settle it before the next scheduled retry."}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={submitPendingSettlement}
              disabled={charging}
              className="bg-rose-500/90 text-white hover:bg-rose-500 rounded-full text-xs h-9 px-4"
            >
              {charging
                ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" />Paying…</>
                : `Pay $${fmt(pendingSettlementCents)}`}
            </Button>
          </div>
          {error && (
            <p className="text-rose-200 text-[11px] mt-1">{error}</p>
          )}
        </div>
      )}
      {hasPastDue && !hasPendingSettlement && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-3 space-y-2">
          <div>
            <p className="text-rose-300 text-xs font-normal uppercase tracking-wider">Past-due balance</p>
            <p className="text-rose-100 text-base font-normal mt-1">${fmt(openInvoicesTotalCents)}</p>
            <p className="text-rose-200/70 text-[11px] mt-1">
              Pay any amount toward it below — with the card on file or a different card. Your plan resumes on its own once it&apos;s cleared.
              {planRestartable ? ' Restart Plan pays all of it at once; Pay off settles the whole trip.' : ' Pay off settles the whole trip, past-due included.'}
            </p>
          </div>
        </div>
      )}

      {/* Mode tabs — matches Stripe's PaymentScheduleDisplay tray (rounded-2xl, icons, brand-800 selected). */}
      <div className={cn('grid gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1', hasPastDue ? 'grid-cols-4' : 'grid-cols-3')}>
        {[
          /** Pay toward past due — only offered (and the default) while past-due rows exist. */
          ...(hasPastDue ? [{ id: 'past_due', label: 'Past due', icon: AlertTriangle, disabled: false, disabledReason: null }] : []),
          { id: 'skip', label: 'Pay ahead', icon: FastForward, disabled: perPaymentCentsHint <= 0 || totalUpcomingCount === 0 || allTabsDisabledByPastDue, disabledReason: allTabsDisabledByPastDue ? 'Pay your past-due balance first' : null },
          /** "Principal" (formerly "Custom") — payments here apply toward the overall balance.
           *  Renamed for clarity vs. the Pay-ahead → Custom Amount sub-tab which targets specific
           *  upcoming installments. */
          { id: 'custom', label: 'Principal', icon: DollarSign, disabled: allTabsDisabledByPastDue, disabledReason: allTabsDisabledByPastDue ? 'Pay your past-due balance first' : null },
          /** Pay off is deliberately NOT gated on past-due — the server settles past-due
           *  rows inside the payoff charge (see comment on allTabsDisabledByPastDue). */
          { id: 'payoff', label: 'Pay off', icon: CheckCircle, disabled: false, disabledReason: null },
        ].map((tab) => {
          const Icon = tab.icon;
          const selected = mode === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              disabled={tab.disabled}
              title={tab.disabled && tab.disabledReason ? tab.disabledReason : undefined}
              onClick={() => {
                setMode(tab.id);
                resetPreview();
                payNonceRef.current = null;
              }}
              className={cn(
                'group relative rounded-xl flex flex-col items-center justify-center gap-1 py-2.5 transition-all duration-200',
                selected
                  ? 'bg-brand-800 text-white shadow-[0_0_14px_-6px_rgba(10,18,51,0.8)]'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]',
                tab.disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-white/50',
              )}
            >
              <Icon className={cn('w-3.5 h-3.5', selected ? 'text-white' : 'text-white/40 group-hover:text-white/60')} />
              <span className="text-[11px] font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Mode content */}
      {mode === 'past_due' && (() => {
        const p = preview?.mode === 'past_due' && !preview?.exceeds_remaining ? preview : null;
        const appliedCents = p ? Number(p.applied_to_past_due_cents) || 0 : Math.min(pastDueCents, openInvoicesTotalCents);
        const stillPastDueCents = p ? Number(p.past_due_remaining_cents) || 0 : Math.max(0, openInvoicesTotalCents - pastDueCents);
        const cured = p ? Boolean(p.past_due_fully_cured) : pastDueCents >= openInvoicesTotalCents;
        const leftoverCents = p ? Number(p.leftover_to_upcoming_cents) || 0 : Math.max(0, pastDueCents - openInvoicesTotalCents);
        return (
          <div className="rounded-2xl border border-rose-500/25 bg-gradient-to-br from-rose-500/[0.08] via-rose-500/[0.03] to-transparent p-4 space-y-3">
            <div>
              <p className="text-white text-sm font-medium leading-tight">Pay toward your past-due balance</p>
              <p className="text-white/60 text-[11px] font-normal mt-1 leading-snug">
                Pay what you can now. It comes straight off the ${fmt(openInvoicesTotalCents)} that&apos;s past due, and your plan resumes on its own once that&apos;s cleared — no restart needed.
              </p>
            </div>
            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Amount to pay ($)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="number"
                  min="5"
                  step="1"
                  value={pastDueAmount}
                  aria-label="Amount to pay toward past due"
                  onChange={(e) => {
                    setPastDueAmount(e.target.value);
                    resetPreview();
                    payNonceRef.current = null;
                  }}
                  placeholder="0.00"
                  className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-rose-400/40"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setPastDueAmount((openInvoicesTotalCents / 100).toFixed(2));
                  resetPreview();
                  payNonceRef.current = null;
                }}
                disabled={charging}
                className="mt-2 text-[11px] text-rose-200/80 hover:text-rose-100 underline underline-offset-2 disabled:opacity-50"
              >
                Pay the full past-due balance (${fmt(openInvoicesTotalCents)})
              </button>
            </div>
            {pastDueCents >= 500 && !preview?.exceeds_remaining && (
              <div className="rounded-xl border border-white/[0.08] bg-black/20 p-3 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/60">Applied to past due</span>
                  <span className="text-white/90 tabular-nums">${fmt(appliedCents)}</span>
                </div>
                {cured ? (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-white/60">After this payment</span>
                    <span className="text-emerald-300">Past due cleared — plan resumes</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-white/60">Still past due after</span>
                    <span className="text-rose-200 tabular-nums">${fmt(stillPastDueCents)}</span>
                  </div>
                )}
                {leftoverCents > 0 && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-white/60">Toward upcoming payments</span>
                    <span className="text-emerald-300 tabular-nums">${fmt(leftoverCents)}</span>
                  </div>
                )}
              </div>
            )}
            {preview?.exceeds_remaining && payoffCents > 0 && (
              <div className="pt-3 border-t border-white/[0.06] space-y-2">
                <p className="text-amber-300/90 text-[11px]">
                  That amount is more than your remaining balance. Use Pay off to settle the trip.
                </p>
                <Button
                  type="button"
                  onClick={handlePayoffSwitch}
                  disabled={charging}
                  className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full h-10 text-xs')}
                >
                  Pay off ${fmt(payoffCents)}
                </Button>
              </div>
            )}
            <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between">
              <span className="text-white/60 text-xs uppercase tracking-wider">You&apos;ll pay today</span>
              <span className="text-white font-normal text-base tabular-nums">${fmt(pastDueCents)}</span>
            </div>
            {pastDueCents > 0 && pastDueCents < 500 && (
              <p className="text-amber-300/80 text-[10px]">Minimum payment is $5.00.</p>
            )}
          </div>
        );
      })()}

      {mode === 'skip' && (
        <div className="space-y-3">
          {/** Pay-ahead sub-modes — parallel to the Stripe form. Both call NMI's existing greedy
           *   `mode='custom'` backend for the custom-amount path; the installments path uses
           *   `mode='skip_installments'` so the integer-N flow stays distinct in the ledger. */}
          {/** Borderless pill — sits inside the Pay-ahead gradient card. An outer border here
           *   would compete with the card's edge and (because `border-white/8` is not a valid
           *   Tailwind class — default scale is 5/10/15) used to render as harsh full-white. */}
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-white/[0.04] p-1">
            {[
              { id: 'installments', label: 'Installments' },
              { id: 'custom', label: 'Custom amount' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                disabled={charging}
                onClick={() => {
                  setPayAheadSubMode(id);
                  resetPreview();
                  payNonceRef.current = null;
                }}
                className={cn(
                  'rounded-lg py-2 text-[11px] font-medium transition-all',
                  payAheadSubMode === id
                    ? 'bg-brand-800/90 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]'
                    : 'text-white/55 hover:text-white/80 hover:bg-white/[0.04]'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {payAheadSubMode === 'installments' && (
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-4 space-y-3">
              <div>
                <p className="text-white/90 text-sm font-medium">Pay upcoming installments early</p>
                <p className="text-white/60 text-[11px] mt-0.5 leading-snug">
                  Same amount per payment. Your next auto-charge moves out so you have more breathing room.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white/60 text-xs uppercase tracking-wider">Skip how many?</p>
                  {perPaymentCentsHint > 0 && (
                    <p className="text-white/35 text-[10px] mt-0.5">${fmt(perPaymentCentsHint)} each</p>
                  )}
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setSkipCount((n) => Math.max(1, n - 1))}
                    disabled={charging || skipCount <= 1}
                    className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] text-white flex items-center justify-center hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-white tabular-nums w-8 text-center text-base">{skipCount}</span>
                  <button
                    type="button"
                    onClick={() => setSkipCount((n) => Math.min(upcomingCap, n + 1))}
                    disabled={charging || skipCount >= upcomingCap}
                    className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] text-white flex items-center justify-center hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-white/60 text-xs uppercase tracking-wider">You'll pay today</span>
                <span className="text-white font-normal text-base tabular-nums">${fmt(skipChargeCents)}</span>
              </div>
              {nextChargeDates?.changed && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/45">Next charge moves</span>
                  <span className="text-white/75 inline-flex items-center gap-1.5 tabular-nums">
                    <span className="text-white/45 line-through">{nextChargeDates.from}</span>
                    <ArrowRight className="w-3 h-3 text-white/35" />
                    <span>{nextChargeDates.to}</span>
                  </span>
                </div>
              )}
              {preview?.would_exceed_deadline && payoffCents > 0 && (
                /** Mirror Stripe's "Pay off $X" CTA: skipping that many would cover everything,
                 *  so offer a one-tap path to switch to Pay-off rather than blocking the user. */
                <div className="pt-3 border-t border-white/[0.06] space-y-2">
                  <p className="text-amber-300/90 text-[11px]">
                    Skipping {skipCount} {skipCount === 1 ? 'installment' : 'installments'} would cover everything remaining.
                  </p>
                  <Button
                    type="button"
                    onClick={handlePayoffSwitch}
                    disabled={charging}
                    className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full h-10 text-xs')}
                  >
                    Pay off ${fmt(payoffCents)}
                  </Button>
                </div>
              )}
            </div>
          )}

          {payAheadSubMode === 'custom' && (() => {
            /** Prefer the server's authoritative `skip_count` / `partial_credit_cents`
             *  (now computed greedily from the live attempts ledger). Fall back to the
             *  schedule-diff if those fields are missing (older deploys). Pre-fix this
             *  re-derived the partial as `payAheadCustomCents - skipCount × perPaymentHint`,
             *  which is wrong after a previous partial credit has left some installments
             *  at non-uniform amounts. */
            const greedyCovers = Number(preview?.skip_count)
              || Math.max(0, (preview?.current_schedule?.num_payments || 0) - (preview?.proposed_schedule?.num_payments || 0));
            const partialCents = Number(preview?.partial_credit_cents)
              || Math.max(0, payAheadCustomCents - greedyCovers * (perPaymentCentsHint || 0));
            const showBreakdown = preview?.mode === 'custom' && payAheadCustomCents > 0 && !preview?.exceeds_remaining;
            return (
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent p-4 space-y-3">
                <div>
                  <p className="text-white text-sm font-medium leading-tight">Pay a custom amount ahead</p>
                  <p className="text-white/60 text-[11px] font-normal mt-1 leading-snug">
                    We'll cover as many full installments as possible, then credit any leftover toward your next payment.
                  </p>
                </div>
                <div>
                  <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Amount to pay ($)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={payAheadCustomAmount}
                      onChange={(e) => {
                        setPayAheadCustomAmount(e.target.value);
                        resetPreview();
                        payNonceRef.current = null;
                      }}
                      placeholder="0.00"
                      className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-500/40"
                    />
                  </div>
                  {perPaymentCentsHint > 0 && (
                    <p className="text-white/30 text-[10px] mt-1.5">Each installment is ${fmt(perPaymentCentsHint)}.</p>
                  )}
                </div>
                {showBreakdown && (
                  <div className="rounded-xl border border-brand-500/20 bg-brand-500/[0.05] p-3 space-y-1.5">
                    {greedyCovers > 0 && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-white/60">Covers</span>
                        <span className="text-white/90 tabular-nums">
                          {greedyCovers} {greedyCovers === 1 ? 'installment' : 'installments'} (${fmt(greedyCovers * (perPaymentCentsHint || 0))})
                        </span>
                      </div>
                    )}
                    {partialCents > 0 && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-white/60">Credit on next payment</span>
                        <span className="text-emerald-300 tabular-nums">${fmt(partialCents)}</span>
                      </div>
                    )}
                  </div>
                )}
                {preview?.exceeds_remaining && payoffCents > 0 && (
                  /** One-tap "Pay off $X" CTA when the typed amount exceeds remaining. Mirrors
                   *  the Stripe form (and the `would_exceed_deadline` UX) so customers can switch
                   *  to Pay-off in one click instead of being stuck with an inline warning. */
                  <div className="pt-3 border-t border-white/[0.06] space-y-2">
                    <p className="text-amber-300/90 text-[11px]">
                      That amount is more than your remaining balance. Use Pay off to settle the trip.
                    </p>
                    <Button
                      type="button"
                      onClick={handlePayoffSwitch}
                      disabled={charging}
                      className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full h-10 text-xs')}
                    >
                      Pay off ${fmt(payoffCents)}
                    </Button>
                  </div>
                )}
                <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="text-white/60 text-xs uppercase tracking-wider">You'll pay today</span>
                  <span className="text-white font-normal text-base tabular-nums">${fmt(payAheadCustomCents)}</span>
                </div>
                {payAheadCustomCents > 0 && payAheadCustomCents < 500 && (
                  <p className="text-amber-300/80 text-[10px]">Minimum payment is $5.00.</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {mode === 'custom' && (
        /** Principal tab — mirrors the Stripe form. Bare layout (no card wrapper) so the input
         *  itself is the focal element. Renamed from "Custom" to "Principal" to communicate that
         *  the payment goes toward the overall balance (vs. specific upcoming installments). */
        <div>
          <label className="block text-white/50 text-xs mb-1">Amount to pay ($)</label>
          <p className="text-white/30 text-[10px] mb-2">
            Pay any amount toward your balance. To pay one installment ahead with no schedule churn, use Pay ahead.
          </p>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="number"
              min="5"
              step="1"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                resetPreview();
              }}
              placeholder="0.00"
              className="pl-9 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-500/40"
            />
          </div>
          {perPaymentCentsHint > 0 && perPaymentCentsHint <= (remainingCentsHint || perPaymentCentsHint) && (
            <button
              type="button"
              onClick={() => {
                setAmount((perPaymentCentsHint / 100).toFixed(2));
                resetPreview();
              }}
              disabled={charging}
              className="mt-2 text-[11px] text-brand-300/80 hover:text-brand-200 underline underline-offset-2 disabled:opacity-50"
            >
              Match one installment (${fmt(perPaymentCentsHint)})
            </button>
          )}
          {parseFloat(amount) > 0 && parseFloat(amount) < 5 && (
            <p className="mt-1.5 text-amber-300/80 text-[10px]">
              Small payments (under $5) leave fractional per-cycle amounts behind. Try Pay ahead, or round up to at least $5.
            </p>
          )}
          {preview?.exceeds_remaining && (
            <p className="text-amber-300 text-xs mt-2">
              That amount is more than your remaining balance. Use Pay off instead.
            </p>
          )}
        </div>
      )}

      {mode === 'payoff' && (
        <div className="rounded-2xl border border-brand-500/25 bg-gradient-to-br from-brand-600/15 via-brand-600/[0.06] to-transparent p-4 space-y-2">
          <p className="text-brand-200 text-sm font-medium">Pay off your trip in full</p>
          <p className="text-brand-200/60 text-[11px] leading-snug">
            Charges your remaining balance now and ends auto-installments — no more upcoming payments.
          </p>
          <div className="flex items-center justify-between border-t border-brand-500/15 pt-3">
            <span className="text-brand-200/70 text-xs">You'll pay today</span>
            <span className="text-white text-base font-medium tabular-nums">${fmt(payoffCents)}</span>
          </div>
        </div>
      )}

      {/* Charge to — card on file (the vault) or a different card charged once. NMI has
          one vault per customer, so there is no saved-card picker; the one-time path
          tokenizes through Collect.js and never stores the card. */}
      <div className={paymentCardGradient.container}>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-3">Charge to</p>
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={() => { setUseDifferentCard(false); setBillingErrors({}); }}
            disabled={charging}
            className={`${paymentCardGradient.cardRowBase} ${!useDifferentCard ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected}`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${!useDifferentCard ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected}`}>
              <CreditCard className={`w-5 h-5 ${!useDifferentCard ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-medium text-white">Card on file</span>
              <span className="text-white/50 font-normal text-sm ml-2">
                {cardOnFile ? `${formatNmiCardBrandLabel(cardOnFile.brand)} •••• ${cardOnFile.last4}` : '(used at booking)'}
              </span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setUseDifferentCard(true)}
            disabled={charging}
            className={`${paymentCardGradient.cardRowBase} ${useDifferentCard ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected}`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${useDifferentCard ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected}`}>
              <CreditCard className={`w-5 h-5 ${useDifferentCard ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-medium text-white">Use a different card</span>
              <span className="block text-white/50 font-normal text-xs mt-0.5">Charged once for this payment only — not saved, and your card on file stays as is.</span>
            </div>
          </button>
        </div>
        {!useDifferentCard && !cardOnFile && (
          <div className="mt-3">
            <NmiCardOnFileDisplay
              flightData={flightData}
              fallbackText="We'll charge the card you used to book this trip. To change it for future installments, use Update Card."
            />
          </div>
        )}
        {useDifferentCard && (
          <div className="mt-3 space-y-3" data-testid="nmi-one-time-card">
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">Card Number</label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-mp-ccnumber" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">Expiration</label>
                <div className={NMI_FIELD_SHELL_CLASS}>
                  <div id="dc-nmi-mp-ccexp" />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">CVV</label>
                <div className={NMI_FIELD_SHELL_CLASS}>
                  <div id="dc-nmi-mp-cvv" />
                </div>
              </div>
            </div>
            {keyLoadError && (
              <p className="text-red-400 text-xs">{keyLoadError}</p>
            )}
            {!keyLoadError && !collectReady && (
              <p className="text-white/40 text-[11px] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Loading secure card form…</p>
            )}
            <BillingAddressFields
              values={billing}
              onChange={(next) => { setBilling(next); if (Object.keys(billingErrors).length) setBillingErrors(validateBillingAddress(next)); }}
              errors={billingErrors}
              disabled={charging}
              idPrefix="dc-nmi-mp-billing"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      {(mode === 'skip' || mode === 'past_due') && loadingPreview && !preview && (
        <div className="flex items-center justify-center py-4 text-white/40 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading schedule…
        </div>
      )}

      {mode === 'skip' && payAheadSubMode === 'installments' && preview && !preview.would_exceed_deadline && (
        <ScheduleComparisonBlock preview={preview} strikethroughCount={skipCount} />
      )}

      {/* `skip_count` is now authoritative from the server's greedy split (custom-mode preview).
          Pre-fix this derived from `current.num_payments - proposed.num_payments`, which is only
          correct when all installments are uniform. After a prior partial credit shrinks the next
          row's `amount_cents`, the diff under-counts strikethroughs by one. */}
      {mode === 'skip' && payAheadSubMode === 'custom' && preview && !preview.exceeds_remaining && (
        <ScheduleComparisonBlock
          preview={preview}
          strikethroughCount={Number(preview?.skip_count) || 0}
        />
      )}

      {mode === 'custom' && preview && !preview.exceeds_remaining && (
        <ScheduleComparisonBlock preview={preview} caption="Current vs new schedule. Confirm to charge." columnLabel="New" />
      )}

      {mode === 'past_due' && preview?.mode === 'past_due' && !preview.exceeds_remaining && (
        <ScheduleComparisonBlock
          preview={preview}
          strikethroughCount={(Number(preview.settle_count) || 0) + (Number(preview.skip_count) || 0)}
          caption="What you owe now vs after this payment. Confirm to charge."
          columnLabel="After"
          summarizeNonUniform
        />
      )}

      {/** Action row — mirrors Stripe's layout. No explicit Cancel (use dialog X icon).
       *   - Pay-ahead / Pay-off: single full-width "Pay $X".
       *   - Principal w/o preview: full-width "Preview new schedule".
       *   - Principal w/ preview: "Pay $X" + "Back" (Back clears preview so the customer can edit). */}
      {(() => {
        const chargingNode = (
          <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Charging…</>
        );
        const payLabelCents = (() => {
          if (mode === 'skip' && payAheadSubMode === 'custom') return payAheadCustomCents;
          if (mode === 'skip') return skipChargeCents;
          if (mode === 'payoff') return payoffCents;
          if (mode === 'custom') return customCents;
          if (mode === 'past_due') return pastDueCents;
          return 0;
        })();
        const payLabel = payLabelCents > 0 ? `Pay $${fmt(payLabelCents)}` : 'Pay';
        const submitDisabled =
          charging
          /** Payoff is exempt from the past-due block — it settles past-due rows in the
           *  same charge (matches the tab-enable logic and the server-side gate). */
          || (allTabsDisabledByPastDue && mode !== 'payoff' && mode !== 'past_due')
          || (mode === 'skip' && payAheadSubMode === 'installments' && (skipChargeCents <= 0 || !preview || preview.would_exceed_deadline))
          || (mode === 'skip' && payAheadSubMode === 'custom' && (payAheadCustomCents <= 0 || !preview || preview.exceeds_remaining))
          || (mode === 'payoff' && payoffCents <= 0)
          || (mode === 'custom' && (customCents <= 0 || !preview || preview.exceeds_remaining))
          /** Below the $5 floor → block (the server rejects with `below_minimum`). */
          || (mode === 'skip' && payAheadSubMode === 'custom' && payAheadCustomCents < 500)
          /** Preview still in flight → block. The breakdown / proposed schedule above
           *  is stale until it returns. Pre-fix you could click Pay before the preview
           *  landed and confirm against an outdated breakdown. */
          || loadingPreview
          /** Preview lags typed amount (mid-debounce) → block. The 500ms debounce on
           *  the custom-amount input means there's a window where `payAheadCustomCents`
           *  is the new value but `preview` reflects the old one. */
          || (mode === 'skip' && payAheadSubMode === 'custom' && previewAmountCents !== null && previewAmountCents !== payAheadCustomCents)
          || (mode === 'custom' && previewAmountCents !== null && previewAmountCents !== customCents)
          /** Pay toward past due: needs a live server preview for the typed amount. */
          || (mode === 'past_due' && (pastDueCents < 500 || !preview || preview.exceeds_remaining || preview.mode !== 'past_due'))
          || (mode === 'past_due' && previewAmountCents !== null && previewAmountCents !== pastDueCents)
          /** Different card: Collect.js hosted fields must be mounted before we can tokenize. */
          || (useDifferentCard && !collectReady);

        if (mode === 'custom' && !preview) {
          return (
            <Button
              type="button"
              onClick={() => fetchPreview({ mode: 'custom', amount: customCents })}
              disabled={loadingPreview || customCents <= 0}
              className={cn(MODAL_PRIMARY_BTN_CLASS, 'w-full')}
            >
              {loadingPreview
                ? (<><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</>)
                : 'Preview new schedule'}
            </Button>
          );
        }

        if (mode === 'custom' && preview) {
          return (
            <div className="flex gap-2.5 pt-2">
              <Button
                type="button"
                onClick={submitCharge}
                disabled={submitDisabled}
                className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
              >
                {charging ? chargingNode : payLabel}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { resetPreview(); payNonceRef.current = null; }}
                disabled={charging}
                className="px-5 h-10 text-xs text-white/60 hover:text-white/90"
              >
                Back
              </Button>
            </div>
          );
        }

        return (
          <div className="flex gap-2.5 pt-2">
            <Button
              type="button"
              onClick={submitCharge}
              disabled={submitDisabled}
              className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
            >
              {charging ? chargingNode : payLabel}
            </Button>
          </div>
        );
      })()}
    </div>
  );
}
